import React, { useState, useMemo } from "react";
import toast from "react-hot-toast";
import { useAuthStore } from "@/store/authStore";
import { CustomersControlPanel } from "../components/customers_control_panel";
import { CustomersStats } from "../components/customers_stats";
import { CustomersTableView } from "../components/customers_table_view";
import { Pagination, ConfirmationModal, TableActionButton } from "@/components/ui";
import { CONTACT_STAGES, getVisibleContactStagesForRole } from "../constants/contactConstants";
// import { AddDesign } from "../../client_contact_stages/sd-design/components/AddDesign";
// import { DesignerUpload } from "../../client_contact_stages/sd-design/components/designer_upload";
import { useContactPermissions } from "../hooks/useContactPermissions";
import { ContactModals } from "../components/contact_modals";
import { RequestRevertModal } from "../components/request_revert_modal";
import { normalizeCustomer } from "../utils/normalization";
import { useContacts, useActivityStats } from "../hooks/useContacts";
import { useCustomerFilters } from "../hooks/useCustomerFilters";
import { useTelecallers } from "../hooks/useTelecallers";
import { useClientPagination } from "@/hooks";
import { useUsers } from "@/hooks";
import { useNavigate } from "react-router-dom";

export default function ContactScreen() {
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const { isAdmin } = useContactPermissions(user);

  const visibleStages = useMemo(() => {
    return getVisibleContactStagesForRole(CONTACT_STAGES, user);
  }, [user]);

  // 1. UI State & Filters
  const {
    search, setSearch, debouncedSearch,
    view, setView,
    viewStyle, setViewStyle,
    page, setPage,
    sortBy, setSortBy,
    filters, setFilters,
    showAdvancedFilters, setShowAdvancedFilters
  } = useCustomerFilters();

  const [isStatsExpanded, setIsStatsExpanded] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [revertRequest, setRevertRequest] = useState(null);
  const [designUploadLead, setDesignUploadLead] = useState(null);

  // 2. Data Fetching
  // 2. Query Params
  const tableParams = useMemo(() => ({
    search: debouncedSearch,
    status: filters.status !== 'all' ? filters.status : undefined,
    activityStatus: filters.activityStatus !== 'all' ? filters.activityStatus : undefined,
    assignedTo: filters.assignee !== 'all' ? filters.assignee : undefined,
    date: filters.date !== 'all' ? filters.date : undefined,
    fromDate: filters.fromDate,
    toDate: filters.toDate,
    sortBy
  }), [debouncedSearch, filters.status, filters.activityStatus, filters.assignee, filters.date, filters.fromDate, filters.toDate, sortBy]);

  const statsParams = useMemo(() => ({
    search: debouncedSearch,
    status: filters.status !== 'all' ? filters.status : undefined,
    activityStatus: filters.activityStatus !== 'all' ? filters.activityStatus : undefined,
    assignedTo: filters.assignee !== 'all' ? filters.assignee : undefined,
    date: filters.date !== 'all' ? filters.date : undefined,
    fromDate: filters.fromDate,
    toDate: filters.toDate
  }), [debouncedSearch, filters.status, filters.activityStatus, filters.assignee, filters.date, filters.fromDate, filters.toDate]);

  const {
    contacts, total, isLoading, refetch,
    importHistory, isHistoryLoading,
    stats: globalStats, isStatsLoading,
    deleteContact, bulkImport, updateContact, deleteImport
  } = useContacts(tableParams, statsParams);

  const { data: activityStats } = useActivityStats(statsParams);

  const { telecallers, loading: telecallersLoading } = useTelecallers();
  const { teamMembers: teamData } = useUsers({ limit: 1000, includeOwner: true });

  // 3. Modal State
  const [addOpen, setAddOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editData, setEditData] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewData, setViewData] = useState(null);
  const [confirmState, setConfirmState] = useState({ isOpen: false, title: "", message: "", onConfirm: null, loading: false });

  const updateStatus = async (customer, targetStatus) => {
    const currentStatus = customer.status === "drop" ? "drop" : (customer.teleStatus || customer.status);
    const currentIndex = CONTACT_STAGES.findIndex(s => s.key === currentStatus);
    const targetIndex = CONTACT_STAGES.findIndex(s => s.key === targetStatus);

    if (currentIndex !== -1 && targetIndex !== -1 && targetIndex < currentIndex) {
      setRevertRequest({ contact: customer, targetStatus });
      return;
    }

    const pipelineSequence = [
      'appointment_scheduled',
      'site_visit',
      'initial_design',
      'initial_quotation',
      'client_approval',
      'converted'
    ];
    const currentPipeIdx = pipelineSequence.indexOf(currentStatus);
    const targetPipeIdx = pipelineSequence.indexOf(targetStatus);

    if (targetPipeIdx !== -1) {
      if (currentPipeIdx === -1 && targetPipeIdx > 0) {
        toast.error("Complete the before step (Appointment Scheduled) to move forward.");
        return;
      }
      if (currentPipeIdx !== -1 && targetPipeIdx > currentPipeIdx + 1) {
        const nextRequiredStageKey = pipelineSequence[currentPipeIdx + 1];
        const nextRequiredStageLabel = CONTACT_STAGES.find(s => s.key === nextRequiredStageKey)?.label || "before step";
        toast.error(`Complete the before step (${nextRequiredStageLabel}) to move forward.`);
        return;
      }
    }

    if (targetStatus === 'initial_quotation') {
      try {
        await updateContact({
          id: customer._id,
          data: { status: targetStatus, teleStatus: targetStatus, notes: "Status changed directly to initial_quotation" }
        });
      } catch (err) {
        toast.error("Failed to update status");
      }
      return;
    }
    setPendingStatus({ customer, targetStatus });
  };

  const confirmStatusUpdate = async (modalData) => {
    if (!pendingStatus) return;
    const { customer, targetStatus } = pendingStatus;
    const { note, ...extraData } = modalData;

    await updateContact({
      id: customer._id,
      data: { status: targetStatus, teleStatus: targetStatus, notes: note, ...extraData }
    });

    setPendingStatus(null);
  };

  const handleDirectRevert = async () => {
    if (!revertRequest) return;
    const { contact, targetStatus } = revertRequest;
    try {
      await updateContact({
        id: contact._id,
        data: {
          status: targetStatus,
          teleStatus: targetStatus,
          notes: `Stage reverted directly by Owner to ${targetStatus}`
        }
      });
      toast.success(`Stage reverted to ${targetStatus}`);
      setRevertRequest(null);
    } catch (err) {
      toast.error("Failed to revert status");
    }
  };

  const [isExporting, setIsExporting] = useState(false);

  const toggleDisableCustomer = (id, currentStatus) => {
    const isEnabling = !!currentStatus;
    setConfirmState({
      isOpen: true,
      title: isEnabling ? "Enable Customer" : "Disable Customer",
      message: `Are you sure you want to ${isEnabling ? 'enable' : 'disable'} this customer? ${isEnabling ? 'They will be restored to the active list.' : 'They will be hidden from the main view.'}`,

      variant: isEnabling ? "info" : "danger",
      onConfirm: async () => {
        setConfirmState(p => ({ ...p, loading: true }));
        try {
          const newStatus = !currentStatus;
          await updateContact({ id, data: { isDisabled: newStatus } });
          toast.success(`Record ${newStatus ? 'disabled' : 'enabled'} successfully`);
        } catch (error) {
          console.error("Disable error:", error);
          toast.error("Failed to update record status");
        } finally {
          setConfirmState({ isOpen: false });
        }
      }
    });
  };

  const loadData = () => {
    refetch();
  };

  // 4. Derived Data (Client-side pagination via hook)
  const filteredContacts = useMemo(() => {
    let result = contacts.filter(c => c.salesType !== 'Direct Sale');

    // 1. Search filter (Name, SD ID, Phone Number)
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      result = result.filter(c => {
        const name = (c.customerName || `${c.firstName || ""} ${c.lastName || ""}`).toLowerCase();
        const sdId = (c.sdId || c.sd_id || c.customId || "").toLowerCase();
        const phone = (c.phoneNumber || c.phone || "").toLowerCase();
        return name.includes(q) || sdId.includes(q) || phone.includes(q);
      });
    }

    // 2. Preset Date Range Filter (T, W, M, Y)
    if (filters.dateRange) {
      const now = new Date();
      const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());

      result = result.filter(c => {
        if (!c.createdAt) return false;
        const createdDate = new Date(c.createdAt);
        const diffTime = now - createdDate;
        const diffDays = diffTime / (1000 * 60 * 60 * 24);

        if (filters.dateRange === 'T') {
          return createdDate >= startOfDay;
        } else if (filters.dateRange === 'W') {
          return diffDays <= 7;
        } else if (filters.dateRange === 'M') {
          return diffDays <= 30;
        } else if (filters.dateRange === 'Y') {
          return diffDays <= 365;
        }
        return true;
      });
    }

    // 3. Custom Date Picker Filter (Exact Date)
    if (filters.exactDate) {
      const exactDateObj = new Date(filters.exactDate);
      const startOfDay = new Date(exactDateObj);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(exactDateObj);
      endOfDay.setHours(23, 59, 59, 999);
      
      result = result.filter(c => {
        if (!c.createdAt) return false;
        const createdTime = new Date(c.createdAt).getTime();
        return createdTime >= startOfDay.getTime() && createdTime <= endOfDay.getTime();
      });
    }

    return result;
  }, [contacts, search, filters.dateRange, filters.exactDate]);
  const { paginatedData: paginatedLeads, totalPages } = useClientPagination(filteredContacts, page, 10);
  const leads = filteredContacts; // Keep for filter options and stats

  const filterOptions = useMemo(() => {
    return {
      locations: [...new Set(leads.map(l => l.city).filter(Boolean))],
      sources: [...new Set(leads.map(l => l.source || 'Manual'))],
      assignees: telecallers.map(t => ({ id: t._id, name: t.name }))
    };
  }, [leads, telecallers]);

  const actionableUnassignedCount = globalStats.unassigned || 0;

  return (
    <div className="flex flex-col gap-2 w-full max-w-full pb-2 overflow-x-hidden">
      {/* Removed Title Header */}


      <div className="flex flex-col gap-2">
        <CustomersControlPanel
          leads={leads}
          user={user}
          search={search} setSearch={setSearch}
          filters={filters} setFilters={setFilters}
          sortBy={sortBy} setSortBy={setSortBy}
          showAdvancedFilters={showAdvancedFilters} setShowAdvancedFilters={setShowAdvancedFilters}
          filterOptions={filterOptions}
          view={view} setView={setView}
          viewStyle={viewStyle} setViewStyle={setViewStyle}
          onAddClick={() => setAddOpen(true)}
          stages={visibleStages}
          totalUnassigned={actionableUnassignedCount}
          activityStats={activityStats}
        />

        {/* Developer : Nithin
            Date : 24-04-2026
            changes: Removed min-h-[400px] to prevent unnecessary down scroll when content is less */}
        <div className="flex-1">
          <div className="flex flex-col gap-4">
              {/* {viewStyle === "kanban" ? (
                <CustomersKanban
                  stages={visibleStages}
                  filtered={paginatedLeads}
                  onCustomerClick={(c) => navigate(`/workspace/${c._id}`)}
                  onStatusChange={updateStatus}
                  onEdit={["field_marketing", "field marketing"].includes((user?.role || "").toLowerCase().trim()) ? undefined : (c) => { setEditData(normalizeCustomer(c)); setEditOpen(true); }}
                  onToggleDisable={["field_marketing", "field marketing"].includes((user?.role || "").toLowerCase().trim()) ? undefined : toggleDisableCustomer}
                  teamMembers={telecallers}
                />
              ) : ( */}
                <CustomersTableView
                  filtered={paginatedLeads}
                  isLoading={isLoading}
                  updateStatus={updateStatus}
                  onUpdateNextStage={async (id, nextStage) => {
                    try {
                      await updateContact({
                        id,
                        data: { status: nextStage, next_stage: nextStage }
                      });
                      toast.success(`Next Stage updated to ${nextStage}`);
                    } catch (err) {
                      toast.error("Failed to update Next Stage");
                    }
                  }}
                  onCustomerClick={(c) => { setViewData(normalizeCustomer(c)); setViewOpen(true); }}
                stages={visibleStages}
                onEdit={(c) => { setEditData(normalizeCustomer(c)); setEditOpen(true); }}
                onToggleDisable={toggleDisableCustomer}
                user={user}
                telecallers={telecallers}
                teamMembers={teamData}
                onUpdateProgressStatus={async (id, progressStatus) => {
                  try {
                    await updateContact({
                      id,
                      data: { progressStatus }
                    });
                    toast.success(`Progress Status updated to ${progressStatus}`);
                  } catch (err) {
                    toast.error("Failed to update progress status");
                  }
                }}
                extraActions={(l) => {
                  const currentProg = l.progressStatus || "Initially Not Started";
                  const isDesignerRole = (user?.role || "").toLowerCase().trim() === "designer" || (user?.role || "").toLowerCase().trim().includes("designer");
                  if (
                    (l.status === 'initial_design' || isDesignerRole) &&
                    (isAdmin || isDesignerRole) &&
                    currentProg !== "Completed"
                  ) {
                    return (
                      <TableActionButton
                        icon={UploadCloud}
                        onClick={() => setDesignUploadLead(l)}
                        title="Upload initial design"
                      />
                    );
                  }
                  return null;
                }}
                onAssignStaff={async (id, staffId) => {
                  await updateContact({
                    id,
                    data: {
                      assignedTo: staffId,
                      telecaller: staffId,
                      status: staffId ? "assigned" : "new"
                    }
                  });
                }}
                onAssignDesigner={async (id, designerId) => {
                  await updateContact({
                    id,
                    data: {
                      designer: designerId
                    }
                  });
                }}
              />
            {/* )} */}
            <div className="border-t border-[#ECE7DE] bg-white py-2 px-4 border-x border-b">
              <Pagination page={page} totalPages={totalPages || 1} onPageChange={setPage} />
            </div>
          </div>
      </div>
    </div>

    <ContactModals
        updateStatus={updateStatus}
        addOpen={addOpen} setAddOpen={setAddOpen}
        editOpen={editOpen} setEditOpen={setEditOpen}
        editData={editData} setEditData={setEditData}
        viewOpen={viewOpen} setViewOpen={setViewOpen}
        viewData={viewData} setViewData={setViewData}
        pendingStatus={pendingStatus} setPendingStatus={setPendingStatus}
        confirmStatusUpdate={confirmStatusUpdate}
        stages={visibleStages}
        user={user}
      />

      {/* {designUploadLead && (
        <DesignerUpload
          isOpen={!!designUploadLead}
          onClose={() => setDesignUploadLead(null)}
          lead={designUploadLead}
        />
      )} */}

      {revertRequest && (
        <RequestRevertModal
          isOpen={true}
          onClose={() => setRevertRequest(null)}
          contact={revertRequest.contact}
          targetStatus={revertRequest.targetStatus}
          onRequested={() => setRevertRequest(null)}
          isOwner={isAdmin || (user?.role || "").toLowerCase().trim() === "owner"}
          onDirectRevert={handleDirectRevert}
        />
      )}

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        onClose={() => setConfirmState({ isOpen: false })}
        onConfirm={confirmState.onConfirm}
        title={confirmState.title}
        message={confirmState.message}
        variant={confirmState.variant}
        loading={confirmState.loading}
      />
    </div>
  );
}






